"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { format, formatDistanceToNow } from "date-fns";
import TradingTools from "./trading-tools";
import { RefreshCw, BarChart3, LineChart } from "lucide-react";
import { cn, formatNumber, allNull } from "@/lib/utils";
import { createChart, CrosshairMode, LineStyle, LineSeries, CandlestickSeries, HistogramSeries, AreaSeries } from "lightweight-charts";
import { MainInfoPrimitive } from "@/components/charts/plugins/main-info";
import { RsiPanePrimitive } from "@/components/charts/plugins/rsi-info";
import { MacdPanePrimitive } from "@/components/charts/plugins/macd-info";
import { IndicatorId, ColorOption, Option, Indicator, getColorOption, getOption } from "./Indicator";

type SeriesId =
    | "mainSeries"
    | "historicalSeries"
    | "buySeries"
    | "sellSeries"
    | "volumeSeries"
    | "historicalVolumeSeries"
    | "maSeries"
    | "emaSeries"
    | "bbUpperSeries"
    | "bbMiddleSeries"
    | "bbLowerSeries";
type RsiSeriesId = "mainSeries" | "bbUpperSeries" | "bbLowerSeries" | "maSeries";
type MacdSeriesId = "mainSeries" | "signalSeries" | "histogramSeries";

function getRangeForTab(activeTab) {
    const now = Date.now() / 1000;
    const day = 24 * 60 * 60;
    let startTime = now;
    switch (activeTab) {
        case "Day":
            startTime = now - 1 * day;
            break;
        case "Week":
            startTime = now - 7 * day;
            break;
        case "Month":
            startTime = now - 30 * day;
            break;
        case "3M":
            startTime = now - 90 * day;
            break;
        case "6M":
            startTime = now - 180 * day;
            break;
        case "1Y":
            startTime = now - 365 * day;
            break;
        case "All":
            startTime = 0;
            break;
    }
    return { from: startTime, to: now };
}

export default function PriceChart({ itemId, priceData: initialPriceData, itemIcon, itemName, currencyIcon }) {
    const [activeTab, setActiveTab] = useState("1Y");
    const [chartType, setChartType] = useState("line");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [priceData, setPriceData] = useState(initialPriceData || []);
    const [metrics, setMetrics] = useState({
        lastPrice: 0,
        priceChange: 0,
        volume24h: 0,
        high24h: 0,
        low24h: 0,
    });
    const [didInit, setDidInit] = useState(false);
    const [indicators, setIndicators] = useState<Map<IndicatorId, Indicator>>(
        new Map([
            [
                "volume",
                {
                    id: "volume",
                    enabled: true,
                    colorOptions: [
                        { id: "buyColor", label: "Buy Color", color: "#4CAF50", seriesKey: "volumeSeries" },
                        { id: "sellColor", label: "Sell Color", color: "#ef4444", seriesKey: "volumeSeries" },
                    ],
                },
            ],
            [
                "historicalVolume",
                {
                    id: "historicalVolume",
                    enabled: true,
                    colorOptions: [
                        { id: "buyColor", label: "Buy Color", color: "#3b82f6", seriesKey: "historicalVolumeSeries" },
                        { id: "sellColor", label: "Sell Color", color: "#3b82f6", seriesKey: "historicalVolumeSeries" },
                    ],
                },
            ],
            [
                "buyPrice",
                {
                    id: "buyPrice",
                    enabled: true,
                    colorOptions: [{ id: "color", label: "Color", color: "#10b981", seriesKey: "buySeries" }],
                },
            ],
            [
                "sellPrice",
                {
                    id: "sellPrice",
                    enabled: true,
                    colorOptions: [{ id: "color", label: "Color", color: "#ef4444", seriesKey: "sellSeries" }],
                },
            ],
            [
                "historicalPrice",
                {
                    id: "historicalPrice",
                    enabled: true,
                    colorOptions: [{ id: "color", label: "Color", color: "#3b82f6", seriesKey: "historicalSeries" }],
                },
            ],
            [
                "rsi",
                {
                    id: "rsi",
                    pane: null,
                    enabled: true,
                    options: [
                        {
                            id: "period",
                            label: "Period",
                            value: 14,
                            type: "input",
                        },
                        {
                            id: "maType",
                            label: "MA Type",
                            value: "SMA",
                            type: "select",
                            options: ["SMA", "EMA", "WMA", "VWMA", "RMA"],
                        },
                        {
                            id: "bbMult",
                            label: "BB Mult",
                            value: 2.0,
                            type: "input",
                        },
                        {
                            id: "maLength",
                            label: "MA Length",
                            value: 14,
                            type: "input",
                        },
                        {
                            id: "overbought",
                            label: "Overbought",
                            value: 70,
                            type: "input",
                        },
                        {
                            id: "oversold",
                            label: "Oversold",
                            value: 30,
                            type: "input",
                        },
                    ],
                    colorOptions: [
                        { id: "color", label: "Color", color: "#7E57C2", seriesKey: "mainSeries" },
                        { id: "maColor", label: "MA Color", color: "#FDD835", seriesKey: "maSeries" },
                        { id: "overboughtColor", label: "Overbought Color", color: "#10b981", seriesKey: "bbUpperSeries" },
                        { id: "oversoldColor", label: "Oversold Color", color: "#ef4444", seriesKey: "bbLowerSeries" },
                    ],
                },
            ],
            [
                "ma",
                {
                    id: "ma",
                    enabled: false,
                    options: [
                        {
                            id: "period",
                            label: "Period",
                            value: 14,
                            type: "input",
                        },
                    ],
                    colorOptions: [{ id: "color", label: "Color", color: "#10b981", seriesKey: "maSeries" }],
                },
            ],
            [
                "ema",
                {
                    id: "ema",
                    enabled: false,
                    options: [
                        {
                            id: "period",
                            label: "Period",
                            value: 14,
                            type: "input",
                        },
                    ],
                    colorOptions: [{ id: "color", label: "Color", color: "#f59e0b", seriesKey: "emaSeries" }],
                },
            ],
            [
                "bb",
                {
                    id: "bb",
                    enabled: false,
                    colorOptions: [
                        { id: "upperColor", label: "Upper Color", color: "#6366f1", seriesKey: "bbUpperSeries" },
                        { id: "middleColor", label: "Middle Color", color: "#6366f1", seriesKey: "bbMiddleSeries" },
                        { id: "lowerColor", label: "Lower Color", color: "#6366f1", seriesKey: "bbLowerSeries" },
                    ],
                    options: [
                        {
                            id: "period",
                            label: "Period",
                            value: 14,
                            type: "input",
                        },
                    ],
                },
            ],
            [
                "macd",
                {
                    id: "macd",
                    pane: null,
                    enabled: true,
                    fast: 12,
                    slow: 26,
                    signal: 9,
                    colorOptions: [
                        { id: "lineColor", label: "Line Color", color: "#3b82f6", seriesKey: "mainSeries" },
                        { id: "signalColor", label: "Signal Color", color: "#f59e0b", seriesKey: "signalSeries" },
                        { id: "histogramUpColor", label: "Histogram Up Color", color: "#10b981", seriesKey: "histogramSeries" },
                        { id: "histogramDownColor", label: "Histogram Down Color", color: "#ef4444", seriesKey: "histogramSeries" },
                    ],
                    options: [
                        {
                            id: "fast",
                            label: "Fast",
                            value: 12,
                            type: "input",
                        },
                        {
                            id: "slow",
                            label: "Slow",
                            value: 26,
                            type: "input",
                        },
                        {
                            id: "signal",
                            label: "Signal",
                            value: 9,
                            type: "input",
                        },
                        {
                            id: "smaSource",
                            label: "SMA Source",
                            value: "SMA",
                            options: ["SMA", "EMA"],
                            type: "select",
                        },
                        {
                            id: "smaSignal",
                            label: "SMA Signal",
                            value: "SMA",
                            options: ["SMA", "EMA"],
                            type: "select",
                        },
                    ],
                },
            ],
        ])
    );
    const indicatorsRef = useRef(indicators);
    useEffect(() => {
        indicatorsRef.current = indicators;
    }, [indicators]);

    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const blankSeries = {
        mainSeries: null,
        historicalSeries: null,
        buySeries: null,
        sellSeries: null,
        volumeSeries: null,
        historicalVolumeSeries: null,
        maSeries: null,
        emaSeries: null,
        bbUpperSeries: null,
        bbMiddleSeries: null,
        bbLowerSeries: null,
    };

    const seriesRef = useRef(new Map<SeriesId, any>());
    const rsiSeriesRef = useRef(new Map<RsiSeriesId, any>());
    const macdSeriesRef = useRef(new Map<MacdSeriesId, any>());

    const colorChangeTimeoutRef = useRef(null);
    const refreshData = async () => {
        if (isRefreshing) return;

        setIsRefreshing(true);
        try {
            const priceDataRes = await fetch(`/api/prices/${itemId}`);
            const priceDataJson = await priceDataRes.json();
            if (priceDataJson.data) {
                setPriceData(priceDataJson.data);
            }
        } catch (error) {
            console.error("Error refreshing data:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const toggleIndicator = (indicatorId) => {
        setIndicators((prev) => {
            const newIndicators = new Map(prev);
            const indicator = newIndicators.get(indicatorId);
            if (indicator) {
                newIndicators.set(indicatorId, { ...indicator, enabled: !indicator.enabled });
            }
            return newIndicators;
        });
    };

    const handleColorChange = (indicator: Indicator, colorOption: ColorOption, newColor: string) => {
        if (colorChangeTimeoutRef.current) {
            clearTimeout(colorChangeTimeoutRef.current);
        }

        colorChangeTimeoutRef.current = setTimeout(() => {
            setIndicators((prevIndicators) => {
                const newIndicators = new Map(prevIndicators);
                const targetIndicator = newIndicators.get(indicator.id);
                if (targetIndicator) {
                    const updatedColorOptions = targetIndicator.colorOptions.map((option) =>
                        option.id === colorOption.id ? { ...option, color: newColor } : option
                    );
                    newIndicators.set(indicator.id, { ...targetIndicator, colorOptions: updatedColorOptions });
                }
                applyColorChanges(indicator, colorOption, newColor);
                return newIndicators;
            });
        }, 1);
    };

    const handleOptionChange = (newIndicator: Indicator) => {
        setIndicators((prevIndicators) => {
            const newIndicators = new Map(prevIndicators);
            newIndicators.set(newIndicator.id, { ...newIndicator });
            return newIndicators;
        });
    };

    const applyColorChanges = (indicator, colorOption, color) => {
        if (!chartRef.current) return;

        let series = null;
        if (indicator.id === "macd") {
            series = macdSeriesRef.current.get(colorOption.seriesKey);
        } else if (indicator.id === "rsi") {
            series = rsiSeriesRef.current.get(colorOption.seriesKey);
        } else {
            series = seriesRef.current.get(colorOption.seriesKey);
        }

        if (series) {
            series.applyOptions({ color });
            return;
        }
    };

    useEffect(() => {
        if (!priceData || priceData.length === 0) return;

        const now = Date.now() / 1000;
        const oneDayAgo = now - 24 * 60 * 60;

        const last24hData = priceData.filter((item) => item.timestamp >= oneDayAgo).sort((a, b) => a.timestamp - b.timestamp);

        if (last24hData.length === 0) return;

        const lastItem = last24hData[last24hData.length - 1];
        const firstItem = last24hData[0];

        const lastPrice = lastItem.avgHighPrice;
        const firstPrice = firstItem.avgHighPrice;
        const priceChange = ((lastPrice - firstPrice) / firstPrice) * 100;

        const high24h = Math.max(...last24hData.map((item) => item.avgHighPrice));
        const low24h = Math.min(...last24hData.map((item) => item.avgLowPrice));

        const volume24h = last24hData.reduce((sum, item) => sum + (item.highPriceVolume || 0) + (item.lowPriceVolume || 0), 0);

        setMetrics({
            lastPrice,
            priceChange,
            volume24h,
            high24h,
            low24h,
        });
    }, [priceData]);

    let AddSeries = (
        SeriesType: any,
        pane: number,
        data: any[],
        seriesOptions: any = {},
        priceScaleConfig: { id?: string; scaleOptions?: any } = {}
    ) => {
        if (!chartRef.current || !SeriesType || !data) {
            console.warn("Missing required parameters for AddSeries");
            return null;
        }

        const series = chartRef.current.addSeries(SeriesType, seriesOptions, pane);

        if (Array.isArray(data) && data.length > 0) {
            series.setData(data);
        }

        if (priceScaleConfig && priceScaleConfig.id) {
            chartRef.current.priceScale(priceScaleConfig.id).applyOptions(priceScaleConfig.scaleOptions || {});
        }

        return series;
    };

    let RemoveSeries = (series) => {
        if (series) {
            chartRef.current.removeSeries(series);
        }
    };

    const formattedData = useMemo(() => {
        if (!priceData.length)
            return {
                lineSeries: [],
                historicalSeries: [],
                buySeries: [],
                sellSeries: [],
                candleSeries: [],
                volumeSeries: [],
                historicalVolumeSeries: [],
            };

        const historicalData = priceData.filter((item) => item.isHistorical);
        const recentData = priceData.filter((item) => !item.isHistorical);

        const historicalSeries = historicalData
            .map((item, idx) => ({
                time: item.timestamp,
                value: item.avgPrice,
                customValues: {
                    previousValue: historicalData[idx - 1]?.avgPrice || item.avgPrice,
                    previousValuePercentChange: ((item.avgPrice - historicalData[idx - 1]?.avgPrice) / historicalData[idx - 1]?.avgPrice) * 100,
                },
            }))
            .filter(({ value }) => !!value);

        const buySeries = recentData
            .map((item, idx) => ({
                time: item.timestamp,
                value: item.avgHighPrice,
                customValues: {
                    previousValue: recentData[idx - 1]?.avgHighPrice || item.avgHighPrice,
                    previousValuePercentChange: ((item.avgHighPrice - recentData[idx - 1]?.avgHighPrice) / recentData[idx - 1]?.avgHighPrice) * 100,
                },
            }))
            .filter(({ value }) => !!value);

        const sellSeries = recentData
            .map((item, idx) => ({
                time: item.timestamp,
                value: item.avgLowPrice,
                customValues: {
                    previousValue: recentData[idx - 1]?.avgLowPrice || item.avgLowPrice,
                    previousValuePercentChange: ((item.avgLowPrice - recentData[idx - 1]?.avgLowPrice) / recentData[idx - 1]?.avgLowPrice) * 100,
                },
            }))
            .filter(({ value }) => !!value);

        const lineSeries = priceData
            .map((item, idx) => ({
                time: item.timestamp,
                value: item.avgHighPrice,
                customValues: {
                    previousValue: priceData[idx - 1]?.avgHighPrice || item.avgHighPrice,
                    previousValuePercentChange: ((item.avgHighPrice - priceData[idx - 1]?.avgHighPrice) / priceData[idx - 1]?.avgHighPrice) * 100,
                },
            }))
            .filter(({ value }) => !!value);

        const hourlyData: {
            [key: number]: {
                timestamp: number;
                high: number;
                low: number;
                open: number;
                close: number;
                prices: number[];
                buyVolume: number;
                sellVolume: number;
                volume: number;
                isHistorical: boolean;
            };
        } = {};
        priceData.forEach((item) => {
            const date = new Date(item.timestamp * 1000);
            const hourKey = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime();

            if (!hourlyData[hourKey]) {
                hourlyData[hourKey] = {
                    timestamp: Math.floor(hourKey / 1000),
                    high: item.avgPrice,
                    low: item.avgPrice,
                    open: item.avgPrice,
                    close: item.avgPrice,
                    prices: [item.avgHighPrice, item.avgPrice, item.avgLowPrice],
                    buyVolume: item.highPriceVolume || 0,
                    sellVolume: item.lowPriceVolume || 0,
                    volume: (item.highPriceVolume || 0) + (item.lowPriceVolume || 0),
                    isHistorical: item.isHistorical,
                };
            } else {
                hourlyData[hourKey].prices.push(item.avgHighPrice, item.avgPrice, item.avgLowPrice);
                hourlyData[hourKey].high = Math.max(hourlyData[hourKey].high, item.avgPrice);
                hourlyData[hourKey].low = Math.min(hourlyData[hourKey].low, item.avgPrice);
                hourlyData[hourKey].close = item.avgPrice;
                hourlyData[hourKey].buyVolume += item.highPriceVolume || 0;
                hourlyData[hourKey].sellVolume += item.lowPriceVolume || 0;
                hourlyData[hourKey].volume += (item.highPriceVolume || 0) + (item.lowPriceVolume || 0);
                hourlyData[hourKey].isHistorical = item.isHistorical;
            }
        });

        const hourlyArray = Object.values(hourlyData).sort((a, b) => a.timestamp - b.timestamp);

        for (let i = 1; i < hourlyArray.length; i++) {
            hourlyArray[i].open = hourlyArray[i - 1].close;
        }

        const candleSeries = hourlyArray.map((item) => ({
            time: item.timestamp,
            open: item.open,
            high: item.high,
            low: item.low,
            close: item.close,
            volume: item.volume,
            volumeColor:
                (item.buyVolume || 0) > (item.sellVolume || 0)
                    ? getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor")
                    : getColorOption(indicators.get("volume")?.colorOptions || [], "sellColor"),
            isHistorical: item.isHistorical,
        }));

        const volumeSeries = recentData.map((item, idx) => {
            const buyVol = item.highPriceVolume || 0;
            const sellVol = item.lowPriceVolume || 0;
            const totalVolume = buyVol + sellVol;
            const color =
                buyVol > sellVol
                    ? getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor")
                    : getColorOption(indicators.get("volume")?.colorOptions || [], "sellColor");

            return {
                time: item.timestamp,
                value: totalVolume,
                color: color,
                customValues: {
                    previousValue: (recentData[idx - 1]?.highPriceVolume || 0) + (recentData[idx - 1]?.lowPriceVolume || 0) || totalVolume,
                    previousValuePercentChange:
                        ((totalVolume - (recentData[idx - 1]?.highPriceVolume || 0) - (recentData[idx - 1]?.lowPriceVolume || 0)) /
                            (recentData[idx - 1]?.highPriceVolume || 0) -
                            (recentData[idx - 1]?.lowPriceVolume || 0)) *
                        100,
                    buyVolume: buyVol,
                    sellVolume: sellVol,
                },
            };
        });

        const historicalVolumeSeries = historicalData.map((item, idx) => {
            return {
                time: item.timestamp,
                value: item.volume || 0,
                color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                customValues: {
                    previousValue: historicalData[idx - 1]?.volume || item.volume,
                    previousValuePercentChange: ((item.volume - historicalData[idx - 1]?.volume) / historicalData[idx - 1]?.volume) * 100,
                },
            };
        });

        return {
            lineSeries,
            historicalSeries,
            buySeries,
            sellSeries,
            candleSeries,
            volumeSeries,
            historicalVolumeSeries,
        };
    }, [priceData, indicators]);

    const technicalIndicators = useMemo(() => {
        if (!priceData.length)
            return {
                ma: [],
                ema: [],
                bb: { upper: [], middle: [], lower: [] },
                rsi: [],
                rsiMA: [],
                rsiBB: { upper: [], lower: [] },
                macd: { macdLine: [], signalLine: [], histogram: [] },
            };

        const prices = priceData.map((item) => item.avgPrice);
        const timestamps = priceData.map((item) => item.timestamp);
        const volumes = priceData.map((item) => item.highPriceVolume + item.lowPriceVolume || 0);

        function sma(src, len) {
            const out = [];
            for (let i = 0; i < src.length; i++) {
                if (i < len - 1) out.push(0);
                else {
                    let sum = 0;
                    for (let j = i - len + 1; j <= i; j++) sum += src[j];
                    out.push(sum / len);
                }
            }
            return out;
        }
        function ema(src, len) {
            const out = [];
            let k = 2 / (len + 1);
            let prev = src[0];
            for (let i = 0; i < src.length; i++) {
                if (i === 0) out.push(src[0]);
                else {
                    prev = src[i] * k + prev * (1 - k);
                    out.push(prev);
                }
            }
            return out;
        }
        function rma(src, len) {
            const out = [];
            let sum = 0;
            for (let i = 0; i < src.length; i++) {
                if (i < len) {
                    sum += src[i];
                    out.push(i === len - 1 ? sum / len : 0);
                } else {
                    out.push((out[i - 1] * (len - 1) + src[i]) / len);
                }
            }
            return out;
        }
        function wma(src, len) {
            const out = [];
            for (let i = 0; i < src.length; i++) {
                if (i < len - 1) out.push(0);
                else {
                    let num = 0,
                        denom = 0;
                    for (let j = 0; j < len; j++) {
                        num += src[i - j] * (len - j);
                        denom += len - j;
                    }
                    out.push(num / denom);
                }
            }
            return out;
        }
        function vwma(src, vol, len) {
            const out = [];
            for (let i = 0; i < src.length; i++) {
                if (i < len - 1) out.push(0);
                else {
                    let num = 0,
                        denom = 0;
                    for (let j = 0; j < len; j++) {
                        num += src[i - j] * vol[i - j];
                        denom += vol[i - j];
                    }
                    out.push(denom === 0 ? 0 : num / denom);
                }
            }
            return out;
        }

        const rsiLength = Number(getOption(indicators.get("rsi"), "period")?.value || 14);
        const rsiSource = prices;
        const changes = [0];
        for (let i = 1; i < rsiSource.length; i++) {
            changes.push(rsiSource[i] - rsiSource[i - 1]);
        }
        const ups = changes.map((c) => Math.max(c, 0));
        const downs = changes.map((c) => -Math.min(c, 0));
        function rmaArr(src, len) {
            const out = [];
            let sum = 0;
            for (let i = 0; i < src.length; i++) {
                if (i < len) {
                    sum += src[i];
                    out.push(i === len - 1 ? sum / len : 0);
                } else {
                    out.push((out[i - 1] * (len - 1) + src[i]) / len);
                }
            }
            return out;
        }
        const upRma = rmaArr(ups, rsiLength);
        const downRma = rmaArr(downs, rsiLength);
        const rsi = [];
        for (let i = 0; i < rsiSource.length; i++) {
            const up = upRma[i];
            const down = downRma[i];
            let value = 0;
            if (i >= rsiLength - 1) {
                if (down === 0) value = 100;
                else if (up === 0) value = 0;
                else value = 100 - 100 / (1 + up / down);
            }
            rsi.push({ time: timestamps[i], value });
        }

        const rsiData = indicators.get("rsi");
        const maType = getOption(rsiData, "maType")?.value;
        const maLength = Number(getOption(rsiData, "maLength")?.value || 14);
        const bbMult = Number(getOption(rsiData, "bbMult")?.value || 2);
        const enableMA = maType !== "None";
        const isBB = maType === "SMA + Bollinger Bands";
        const rsiVals = rsi.map((d) => d.value);
        let rsiMA = [];
        if (enableMA) {
            switch (maType) {
                case "SMA":
                case "SMA + Bollinger Bands":
                    rsiMA = sma(rsiVals, maLength);
                    break;
                case "EMA":
                    rsiMA = ema(rsiVals, maLength);
                    break;
                case "SMMA (RMA)":
                    rsiMA = rma(rsiVals, maLength);
                    break;
                case "WMA":
                    rsiMA = wma(rsiVals, maLength);
                    break;
                case "VWMA":
                    rsiMA = vwma(rsiVals, volumes, maLength);
                    break;
                default:
                    rsiMA = [];
            }
        }
        let rsiBB = { upper: [], lower: [] };
        if (isBB) {
            for (let i = 0; i < rsiVals.length; i++) {
                if (i < maLength - 1) {
                    rsiBB.upper.push(0);
                    rsiBB.lower.push(0);
                } else {
                    let mean = rsiMA[i];
                    let sumSq = 0;
                    for (let j = i - maLength + 1; j <= i; j++) {
                        sumSq += Math.pow(rsiVals[j] - mean, 2);
                    }
                    let stdev = Math.sqrt(sumSq / maLength);
                    rsiBB.upper.push(mean + stdev * bbMult);
                    rsiBB.lower.push(mean - stdev * bbMult);
                }
            }
        }
        const rsiArr = rsi.map((d, i) => ({ time: timestamps[i], value: d.value }));
        const rsiMAArr = rsiMA.map((v, i) => ({ time: timestamps[i], value: v }));
        const rsiBBUpperArr = rsiBB.upper.map((v, i) => ({ time: timestamps[i], value: v }));
        const rsiBBLowerArr = rsiBB.lower.map((v, i) => ({ time: timestamps[i], value: v }));

        const ma = [];
        if (indicators.get("ma")?.enabled) {
            const period = Number(getOption(indicators.get("ma"), "period")?.value || 14);
            for (let i = period - 1; i < prices.length; i++) {
                const slice = prices.slice(i - period + 1, i + 1);
                const sum = slice.reduce((total, price) => total + price, 0);
                ma.push({ time: timestamps[i], value: sum / period });
            }
        }

        const emaArr = [];
        if (indicators.get("ema")?.enabled) {
            const period = Number(getOption(indicators.get("ema"), "period")?.value || 14);
            if (prices.length >= period) {
                let emaValue = prices.slice(0, period).reduce((total, price) => total + price, 0) / period;
                emaArr.push({
                    time: timestamps[period - 1],
                    value: emaValue,
                });
                const multiplier = 2 / (period + 1);
                for (let i = period; i < prices.length; i++) {
                    emaValue = (prices[i] - emaValue) * multiplier + emaValue;
                    emaArr.push({
                        time: timestamps[i],
                        value: emaValue,
                    });
                }
            }
        }

        const bb = { upper: [], middle: [], lower: [] };
        if (indicators.get("bb")?.enabled) {
            const period = Number(getOption(indicators.get("bb"), "period")?.value || 14);
            const multiplier = 2;
            for (let i = period - 1; i < prices.length; i++) {
                const slice = prices.slice(i - period + 1, i + 1);
                const sum = slice.reduce((total, price) => total + price, 0);
                const smaVal = sum / period;
                const squaredDifferences = slice.map((price) => Math.pow(price - smaVal, 2));
                const variance = squaredDifferences.reduce((total, val) => total + val, 0) / period;
                const stdDev = Math.sqrt(variance);
                bb.middle.push({
                    time: timestamps[i],
                    value: smaVal,
                });
                bb.upper.push({
                    time: timestamps[i],
                    value: smaVal + multiplier * stdDev,
                });
                bb.lower.push({
                    time: timestamps[i],
                    value: smaVal - multiplier * stdDev,
                });
            }
        }

        const macd = { macdLine: [], signalLine: [], histogram: [] };
        if (indicators.get("macd")?.enabled) {
            const fastPeriod = getOption(indicators.get("macd"), "fast")?.value;
            const slowPeriod = getOption(indicators.get("macd"), "slow")?.value;
            const signalPeriod = getOption(indicators.get("macd"), "signal")?.value;
            const macdMaType = getOption(indicators.get("macd"), "smaSource")?.value === "SMA" ? sma : ema;
            const signalMaType = getOption(indicators.get("macd"), "smaSignal")?.value === "SMA" ? sma : ema;
            const fastMa = macdMaType(prices, fastPeriod);
            const slowMa = macdMaType(prices, slowPeriod);
            const macdArr = fastMa.map((v, i) => v - slowMa[i]);
            const signalArr = signalMaType(macdArr, signalPeriod);
            for (let i = 0; i < macdArr.length; i++) {
                macd.macdLine.push({ time: timestamps[i], value: macdArr[i] });
                macd.signalLine.push({ time: timestamps[i], value: signalArr[i] });
                let hist = macdArr[i] - signalArr[i];
                let color = "#FF5252";
                if (i > 0) {
                    if (hist >= 0) color = macd.macdLine[i - 1].value < hist ? "#26A69A" : "#B2DFDB";
                    else color = macd.macdLine[i - 1].value < hist ? "#FFCDD2" : "#FF5252";
                }
                macd.histogram.push({
                    time: timestamps[i],
                    value: hist,
                    color,
                    customValues: { previousValue: macd.histogram[i - 1]?.value || hist },
                });
            }
        }
        return {
            ma,
            ema: emaArr,
            bb,
            rsi: rsiArr,
            rsiMA: rsiMAArr,
            rsiBB: { upper: rsiBBUpperArr, lower: rsiBBLowerArr },
            macd,
        };
    }, [priceData, indicators]);

    const ClearChart = () => {
        if (chartRef.current) {
            Object.entries(seriesRef.current).forEach(([key, series]) => {
                if (series === null) return;
                RemoveSeries(series);
            });

            seriesRef.current = new Map();
        }
    };

    const InitChartSeries = () => {
        const mainPriceScaleOptions = {
            id: "mainGraph",
            scaleOptions: { scaleMargins: { top: 0.2, bottom: 0.2 } },
            autoScale: false,
        };

        if (chartType === "line") {
            seriesRef.current.set(
                "historicalSeries",
                AddSeries(
                    AreaSeries,
                    0,
                    formattedData.historicalSeries,
                    {
                        topColor: `${getColorOption(indicators.get("historicalPrice")?.colorOptions || [], "color")}40`,
                        bottomColor: `${getColorOption(indicators.get("historicalPrice")?.colorOptions || [], "color")}00`,
                        lineColor: getColorOption(indicators.get("historicalPrice")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "historicalPrice",
                    },
                    mainPriceScaleOptions
                )
            );

            seriesRef.current.set(
                "buySeries",
                AddSeries(
                    LineSeries,
                    0,
                    formattedData.buySeries,
                    {
                        color: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "buyPrice",
                    },
                    mainPriceScaleOptions
                )
            );

            seriesRef.current.set(
                "sellSeries",
                AddSeries(
                    LineSeries,
                    0,
                    formattedData.sellSeries,
                    {
                        color: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "sellPrice",
                    },
                    mainPriceScaleOptions
                )
            );

            const volumePriceScaleOptions = {
                id: "volume",
                scaleOptions: { scaleMargins: { top: 0.8, bottom: 0 } },
            };

            if (indicators.get("volume")?.enabled) {
                seriesRef.current.set(
                    "historicalVolumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.historicalVolumeSeries,
                        {
                            color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "historicalVolume",
                        },
                        volumePriceScaleOptions
                    )
                );

                seriesRef.current.set(
                    "volumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.volumeSeries,
                        {
                            color: getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "volume",
                        },
                        volumePriceScaleOptions
                    )
                );
            }
        } else {
            seriesRef.current.set(
                "mainSeries",
                AddSeries(
                    CandlestickSeries,
                    0,
                    formattedData.candleSeries,
                    {
                        upColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        downColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        borderUpColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        borderDownColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        wickUpColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        wickDownColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        priceScaleId: "mainGraph",
                        title: "candle",
                    },
                    mainPriceScaleOptions
                )
            );

            const volumePriceScaleOptions = {
                id: "volume",
                scaleOptions: { scaleMargins: { top: 0.8, bottom: 0 } },
            };

            if (indicators.get("volume")?.enabled) {
                seriesRef.current.set(
                    "historicalVolumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.candleSeries
                            .filter((item) => item.isHistorical)
                            .map((item) => ({
                                time: item.time,
                                value: item.volume,
                                color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            })),
                        {
                            color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "historicalVolumeCandle",
                        },
                        volumePriceScaleOptions
                    )
                );

                seriesRef.current.set(
                    "volumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.candleSeries
                            .filter((item) => !item.isHistorical)
                            .map((item) => ({
                                time: item.time,
                                value: item.volume,
                                color: item.volumeColor,
                            })),
                        {
                            color: getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "volumeCandle",
                        },
                        volumePriceScaleOptions
                    )
                );
            }
        }

        if (indicators.get("ma")?.enabled) {
            seriesRef.current.set(
                "maSeries",
                AddSeries(
                    LineSeries,
                    0,
                    technicalIndicators.ma,
                    {
                        color: getColorOption(indicators.get("ma")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "ma",
                    },
                    mainPriceScaleOptions
                )
            );
        }

        if (indicators.get("ema")?.enabled) {
            seriesRef.current.set(
                "emaSeries",
                AddSeries(
                    LineSeries,
                    0,
                    technicalIndicators.ema,
                    {
                        color: getColorOption(indicators.get("ema")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "ema",
                    },
                    mainPriceScaleOptions
                )
            );
        }

        if (indicators.get("bb")?.enabled) {
            if (technicalIndicators.bb.upper.length > 0) {
                seriesRef.current.set(
                    "bbUpperSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.upper,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "upperColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbUpper",
                        },
                        mainPriceScaleOptions
                    )
                );
            }

            if (technicalIndicators.bb.middle.length > 0) {
                seriesRef.current.set(
                    "bbMiddleSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.middle,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "middleColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbMiddle",
                        },
                        mainPriceScaleOptions
                    )
                );
            }

            if (technicalIndicators.bb.lower.length > 0) {
                seriesRef.current.set(
                    "bbLowerSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.lower,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "lowerColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbLower",
                        },
                        mainPriceScaleOptions
                    )
                );
            }
        }
    };

    const toggleHistoricalSeries = (enabled) => {};

    const toggleBuySeries = (enabled) => {
        if (chartType === "line") {
            seriesRef.current.set(
                "buySeries",
                AddSeries(
                    LineSeries,
                    0,
                    formattedData.buySeries,
                    {
                        color: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "buyPrice",
                    },
                    mainPriceScaleOptions
                )
            );

            seriesRef.current.set(
                "sellSeries",
                AddSeries(
                    LineSeries,
                    0,
                    formattedData.sellSeries,
                    {
                        color: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "sellPrice",
                    },
                    mainPriceScaleOptions
                )
            );

            const volumePriceScaleOptions = {
                id: "volume",
                scaleOptions: { scaleMargins: { top: 0.8, bottom: 0 } },
            };

            if (indicators.get("volume")?.enabled) {
                seriesRef.current.set(
                    "historicalVolumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.historicalVolumeSeries,
                        {
                            color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "historicalVolume",
                        },
                        volumePriceScaleOptions
                    )
                );

                seriesRef.current.set(
                    "volumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.volumeSeries,
                        {
                            color: getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "volume",
                        },
                        volumePriceScaleOptions
                    )
                );
            }
        } else {
            seriesRef.current.set(
                "mainSeries",
                AddSeries(
                    CandlestickSeries,
                    0,
                    formattedData.candleSeries,
                    {
                        upColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        downColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        borderUpColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        borderDownColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        wickUpColor: getColorOption(indicators.get("buyPrice")?.colorOptions || [], "color"),
                        wickDownColor: getColorOption(indicators.get("sellPrice")?.colorOptions || [], "color"),
                        priceScaleId: "mainGraph",
                        title: "candle",
                    },
                    mainPriceScaleOptions
                )
            );

            const volumePriceScaleOptions = {
                id: "volume",
                scaleOptions: { scaleMargins: { top: 0.8, bottom: 0 } },
            };
        }
    };

    const toggleVolumeSeries = (enabled) => {
        if (enabled) {
            const volumePriceScaleOptions = {
                id: "volume",
                scaleOptions: { scaleMargins: { top: 0.8, bottom: 0 } },
            };

            if (chartType === "line") {
                seriesRef.current.set(
                    "historicalVolumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.historicalVolumeSeries,
                        {
                            color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "historicalVolume",
                        },
                        volumePriceScaleOptions
                    )
                );

                seriesRef.current.set(
                    "volumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.volumeSeries,
                        {
                            color: getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "volume",
                        },
                        volumePriceScaleOptions
                    )
                );
            } else {
                seriesRef.current.set(
                    "historicalVolumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.candleSeries
                            .filter((item) => item.isHistorical)
                            .map((item) => ({
                                time: item.time,
                                value: item.volume,
                                color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            })),
                        {
                            color: getColorOption(indicators.get("historicalVolume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "historicalVolumeCandle",
                        },
                        volumePriceScaleOptions
                    )
                );
                seriesRef.current.set(
                    "volumeSeries",
                    AddSeries(
                        HistogramSeries,
                        0,
                        formattedData.candleSeries
                            .filter((item) => !item.isHistorical)
                            .map((item) => ({ time: item.time, value: item.volume, color: item.volumeColor })),
                        {
                            color: getColorOption(indicators.get("volume")?.colorOptions || [], "buyColor"),
                            priceFormat: { type: "volume" },
                            priceScaleId: "volume",
                            title: "volumeCandle",
                        },
                        volumePriceScaleOptions
                    )
                );
            }
        } else {
            RemoveSeries(seriesRef.current.get("historicalVolumeSeries"));
            RemoveSeries(seriesRef.current.get("volumeSeries"));
            seriesRef.current.delete("historicalVolumeSeries");
            seriesRef.current.delete("volumeSeries");
        }
    };

    const mainPriceScaleOptions = {
        id: "mainGraph",
        scaleOptions: { scaleMargins: { top: 0.2, bottom: 0.2 } },
        autoScale: false,
    };

    const toggleMaSeries = (enabled) => {
        if (enabled) {
            seriesRef.current.set(
                "maSeries",
                AddSeries(
                    LineSeries,
                    0,
                    technicalIndicators.ma,
                    {
                        color: getColorOption(indicators.get("ma")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "ma",
                    },
                    mainPriceScaleOptions
                )
            );
        } else {
            RemoveSeries(seriesRef.current.get("maSeries"));
            seriesRef.current.delete("maSeries");
        }
    };
    const toggleEmaSeries = (enabled) => {
        if (enabled) {
            const mainPriceScaleOptions = { id: "mainGraph", scaleOptions: { scaleMargins: { top: 0.2, bottom: 0.2 } }, autoScale: false };
            seriesRef.current.set(
                "emaSeries",
                AddSeries(
                    LineSeries,
                    0,
                    technicalIndicators.ema,
                    {
                        color: getColorOption(indicators.get("ema")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceScaleId: "mainGraph",
                        title: "ema",
                    },
                    mainPriceScaleOptions
                )
            );
        } else {
            RemoveSeries(seriesRef.current.get("emaSeries"));
            seriesRef.current.delete("emaSeries");
        }
    };
    const toggleBbSeries = (enabled) => {
        if (enabled) {
            const mainPriceScaleOptions = { id: "mainGraph", scaleOptions: { scaleMargins: { top: 0.2, bottom: 0.2 } }, autoScale: false };
            if (technicalIndicators.bb.upper.length > 0) {
                seriesRef.current.set(
                    "bbUpperSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.upper,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "upperColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbUpper",
                        },
                        mainPriceScaleOptions
                    )
                );
            }
            if (technicalIndicators.bb.middle.length > 0) {
                seriesRef.current.set(
                    "bbMiddleSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.middle,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "middleColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbMiddle",
                        },
                        mainPriceScaleOptions
                    )
                );
            }
            if (technicalIndicators.bb.lower.length > 0) {
                seriesRef.current.set(
                    "bbLowerSeries",
                    AddSeries(
                        LineSeries,
                        0,
                        technicalIndicators.bb.lower,
                        {
                            color: getColorOption(indicators.get("bb")?.colorOptions || [], "lowerColor"),
                            lineWidth: 1,
                            priceScaleId: "mainGraph",
                            title: "bbLower",
                        },
                        mainPriceScaleOptions
                    )
                );
            }
        } else {
            RemoveSeries(seriesRef.current.get("bbUpperSeries"));
            RemoveSeries(seriesRef.current.get("bbMiddleSeries"));
            RemoveSeries(seriesRef.current.get("bbLowerSeries"));
            seriesRef.current.delete("bbUpperSeries");
            seriesRef.current.delete("bbMiddleSeries");
            seriesRef.current.delete("bbLowerSeries");
        }
    };

    // initialize chart
    useEffect(() => {
        if (!chartContainerRef.current) return;

        if (!chartRef.current) {
            chartRef.current = createChart(chartContainerRef.current, {
                autoSize: true,
                width: chartContainerRef.current.clientWidth,
                height: chartContainerRef.current.clientHeight,
                layout: {
                    background: { color: "transparent" },
                    textColor: "rgba(255, 255, 255, 0.7)",
                    panes: {
                        separatorColor: "rgba(255, 255, 255, 0.1)",
                        separatorHoverColor: "rgba(100, 150, 255, 0.4)",
                        enableResize: true,
                    },
                },
                grid: {
                    vertLines: { color: "rgba(255, 255, 255, 0.1)" },
                    horzLines: { color: "rgba(255, 255, 255, 0.1)" },
                },
                crosshair: {
                    mode: CrosshairMode.Normal,
                    vertLine: {
                        width: 1,
                        color: "rgba(255, 255, 255, 0.5)",
                        style: LineStyle.Dashed,
                    },
                    horzLine: {
                        width: 1,
                        color: "rgba(255, 255, 255, 0.5)",
                        style: LineStyle.Dashed,
                    },
                },
                timeScale: {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    timeVisible: true,
                    secondsVisible: false,
                    rightOffset: 5,
                    barSpacing: 10,
                    fixLeftEdge: false,
                    lockVisibleTimeRangeOnResize: false,
                    rightBarStaysOnScroll: false,
                    borderVisible: true,
                    visible: true,
                },
                rightPriceScale: {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                    autoScale: true,
                },
                handleScroll: {
                    mouseWheel: true,
                    pressedMouseMove: true,
                    horzTouchDrag: true,
                    vertTouchDrag: true,
                },
                handleScale: {
                    axisPressedMouseMove: {
                        time: true,
                        price: true,
                    },
                    mouseWheel: true,
                    pinch: true,
                },
            });
        }

        InitChartSeries();

        setIndicators((prev) => {
            const newIndicators = new Map(prev);
            const rsiIndicator = newIndicators.get("rsi");
            const macdIndicator = newIndicators.get("macd");
            if (rsiIndicator) {
                newIndicators.set("rsi", { ...rsiIndicator, pane: null });
            }
            if (macdIndicator) {
                newIndicators.set("macd", { ...macdIndicator, pane: null });
            }
            return newIndicators;
        });

        setDidInit(true);

        return () => {};
    }, []);

    useEffect(() => {
        if (!didInit) return;
        toggleVolumeSeries(indicators.get("volume")?.enabled);
    }, [indicators.get("volume")?.enabled]);

    useEffect(() => {
        if (!didInit) return;
        toggleMaSeries(indicators.get("ma")?.enabled);
    }, [indicators.get("ma")?.enabled]);

    useEffect(() => {
        if (!didInit) return;
        toggleEmaSeries(indicators.get("ema")?.enabled);
    }, [indicators.get("ema")?.enabled]);

    useEffect(() => {
        if (!didInit) return;
        toggleBbSeries(indicators.get("bb")?.enabled);
    }, [indicators.get("bb")?.enabled]);

    useEffect(() => {
        if (!chartRef.current) return;

        if (indicators.get("rsi")?.enabled && !indicators.get("rsi")?.pane) {
            if (rsiSeriesRef.current.get("mainSeries")) {
                RemoveSeries(rsiSeriesRef.current.get("mainSeries"));
                rsiSeriesRef.current.delete("mainSeries");
            }
            if (rsiSeriesRef.current.get("maSeries")) {
                RemoveSeries(rsiSeriesRef.current.get("maSeries"));
                rsiSeriesRef.current.delete("maSeries");
            }

            const paneCount = chartRef.current.panes().length;
            let rsiPaneIndex = paneCount;

            if (technicalIndicators.rsi.length > 0) {
                rsiSeriesRef.current.set(
                    "mainSeries",
                    AddSeries(LineSeries, rsiPaneIndex, technicalIndicators.rsi, {
                        color: getColorOption(indicators.get("rsi")?.colorOptions || [], "color"),
                        lineWidth: 1,
                        priceLineVisible: true,
                        title: "rsi",
                    })
                );
            }

            if (technicalIndicators.rsiMA.length > 0) {
                rsiSeriesRef.current.set(
                    "maSeries",
                    AddSeries(LineSeries, rsiPaneIndex, technicalIndicators.rsiMA, {
                        color: getColorOption(indicators.get("rsi")?.colorOptions || [], "maColor"),
                        lineWidth: 1,
                        priceLineVisible: true,
                        title: "rsiMA",
                    })
                );
            }

            setIndicators((prev) => {
                const newIndicators = new Map(prev);
                const rsiIndicator = newIndicators.get("rsi");
                if (rsiIndicator) {
                    newIndicators.set("rsi", { ...rsiIndicator, pane: rsiPaneIndex });
                }
                return newIndicators;
            });
        } else if (!indicators.get("rsi")?.enabled && indicators.get("rsi")?.pane) {
            if (rsiSeriesRef.current.get("mainSeries")) {
                RemoveSeries(rsiSeriesRef.current.get("mainSeries"));
                rsiSeriesRef.current.delete("mainSeries");
            }

            if (rsiSeriesRef.current.get("maSeries")) {
                RemoveSeries(rsiSeriesRef.current.get("maSeries"));
                rsiSeriesRef.current.delete("maSeries");
            }

            rsiPanePrimitiveRef.current = null;
            if (macdPanePrimitiveRef.current) {
                macdPanePrimitiveRef.current = null;
            }

            setIndicators((prev) => {
                const newIndicators = new Map(prev);
                const rsiIndicator = newIndicators.get("rsi");
                const macdIndicator = newIndicators.get("macd");
                if (rsiIndicator) {
                    newIndicators.set("rsi", { ...rsiIndicator, pane: null });
                }
                if (macdIndicator) {
                    newIndicators.set("macd", { ...macdIndicator, pane: null });
                }
                return newIndicators;
            });
        }

        if (rsiSeriesRef.current.get("mainSeries")) {
            rsiSeriesRef.current.get("mainSeries").setData(technicalIndicators.rsi);
        }

        if (rsiSeriesRef.current.get("maSeries")) {
            rsiSeriesRef.current.get("maSeries").setData(technicalIndicators.rsiMA);
        }

        return () => {};
    }, [indicators.get("rsi")?.enabled, technicalIndicators.rsi, technicalIndicators.rsiMA]);

    useEffect(() => {
        if (!chartRef.current) return;

        if (indicators.get("macd")?.enabled && !indicators.get("macd")?.pane) {
            if (macdSeriesRef.current.get("mainSeries")) {
                RemoveSeries(macdSeriesRef.current.get("mainSeries"));
                macdSeriesRef.current.delete("mainSeries");
            }
            if (macdSeriesRef.current.get("signalSeries")) {
                RemoveSeries(macdSeriesRef.current.get("signalSeries"));
                macdSeriesRef.current.delete("signalSeries");
            }
            if (macdSeriesRef.current.get("histogramSeries")) {
                RemoveSeries(macdSeriesRef.current.get("histogramSeries"));
                macdSeriesRef.current.delete("histogramSeries");
            }

            const paneCount = chartRef.current.panes().length;
            let macdPaneIndex = paneCount;

            if (technicalIndicators.macd.macdLine.length > 0) {
                macdSeriesRef.current.set(
                    "mainSeries",
                    AddSeries(LineSeries, macdPaneIndex, technicalIndicators.macd.macdLine, {
                        color: getColorOption(indicators.get("macd")?.colorOptions || [], "lineColor"),
                        lineWidth: 1,
                        priceLineVisible: false,
                        title: "macdLine",
                    })
                );
            }

            if (technicalIndicators.macd.signalLine.length > 0) {
                macdSeriesRef.current.set(
                    "signalSeries",
                    AddSeries(LineSeries, macdPaneIndex, technicalIndicators.macd.signalLine, {
                        color: getColorOption(indicators.get("macd")?.colorOptions || [], "signalColor"),
                        lineWidth: 1,
                        priceLineVisible: false,
                        title: "macdSignal",
                    })
                );
            }

            if (technicalIndicators.macd.histogram.length > 0) {
                macdSeriesRef.current.set(
                    "histogramSeries",
                    AddSeries(HistogramSeries, macdPaneIndex, technicalIndicators.macd.histogram, {
                        title: "macdHistogram",
                        priceFormat: {
                            type: "volume",
                        },
                        priceLineVisible: false,
                    })
                );
            }

            setIndicators((prev) => {
                const newIndicators = new Map(prev);
                const macdIndicator = newIndicators.get("macd");
                if (macdIndicator) {
                    newIndicators.set("macd", { ...macdIndicator, pane: macdPaneIndex });
                }
                return newIndicators;
            });
        } else if (!indicators.get("macd")?.enabled && indicators.get("macd")?.pane) {
            if (macdSeriesRef.current.get("mainSeries")) {
                RemoveSeries(macdSeriesRef.current.get("mainSeries"));
                macdSeriesRef.current.delete("mainSeries");
            }

            if (macdSeriesRef.current.get("signalSeries")) {
                RemoveSeries(macdSeriesRef.current.get("signalSeries"));
                macdSeriesRef.current.delete("signalSeries");
            }

            if (macdSeriesRef.current.get("histogramSeries")) {
                RemoveSeries(macdSeriesRef.current.get("histogramSeries"));
                macdSeriesRef.current.delete("histogramSeries");
            }

            macdPanePrimitiveRef.current = null;
            if (rsiPanePrimitiveRef.current) {
                rsiPanePrimitiveRef.current = null;
            }

            setIndicators((prev) => {
                const newIndicators = new Map(prev);
                const macdIndicator = newIndicators.get("macd");
                const rsiIndicator = newIndicators.get("rsi");
                if (macdIndicator) {
                    newIndicators.set("macd", { ...macdIndicator, pane: null });
                }
                if (rsiIndicator) {
                    newIndicators.set("rsi", { ...rsiIndicator, pane: null });
                }
                return newIndicators;
            });
        }

        return () => {};
    }, [indicators, technicalIndicators.macd]);

    useEffect(() => {
        if (!chartRef.current) return;

        if (priceData.length > 0) {
            let { from, to } = getRangeForTab(activeTab);
            chartRef.current.timeScale().setVisibleRange({
                from,
                to,
            });
        } else {
            chartRef.current.timeScale().fitContent();
        }
    }, [activeTab]);

    const mainPanePrimitiveRef = useRef(null);
    const rsiPanePrimitiveRef = useRef(null);
    const macdPanePrimitiveRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return;

        if (!mainPanePrimitiveRef.current) {
            mainPanePrimitiveRef.current = new MainInfoPrimitive({
                padding: {
                    x: 8,
                    y: 8,
                },
                chartType,
                seriesRef,
                itemIcon,
                itemName,
                currencyIcon,
                activeTab,
                indicators,
            });
        }

        if (!rsiPanePrimitiveRef.current && indicators.get("rsi")?.enabled) {
            rsiPanePrimitiveRef.current = new RsiPanePrimitive({
                padding: {
                    x: 8,
                    y: 8,
                },
                chartType,
                seriesRef: rsiSeriesRef,
                itemIcon,
                itemName,
                currencyIcon,
                activeTab,
                indicators: indicatorsRef,
            });
        }

        if (!macdPanePrimitiveRef.current && indicators.get("macd")?.enabled) {
            macdPanePrimitiveRef.current = new MacdPanePrimitive({
                padding: { x: 8, y: 8 },
                chartType,
                seriesRef: macdSeriesRef,
                itemIcon,
                itemName,
                currencyIcon,
                activeTab,
                indicators,
            });
        }

        const panes = chartRef.current.panes();
        const mainPane = panes[0];

        if (mainPane && mainPanePrimitiveRef.current) {
            mainPane.attachPrimitive(mainPanePrimitiveRef.current);
        }

        if (indicators.get("rsi")?.enabled && indicators.get("rsi")?.pane !== null && rsiPanePrimitiveRef.current) {
            const rsiPane = panes[indicators.get("rsi")?.pane];
            if (rsiPane) {
                rsiPane.attachPrimitive(rsiPanePrimitiveRef.current);
            }
        }

        if (indicators.get("macd")?.enabled && indicators.get("macd")?.pane !== null && macdPanePrimitiveRef.current) {
            const macdPane = panes[indicators.get("macd")?.pane];
            if (macdPane) {
                macdPane.attachPrimitive(macdPanePrimitiveRef.current);
            }
        }

        return () => {
            if (mainPane && mainPanePrimitiveRef.current) {
                mainPane.detachPrimitive(mainPanePrimitiveRef.current);
            }

            if (indicators.get("rsi")?.enabled && indicators.get("rsi")?.pane !== null && rsiPanePrimitiveRef.current) {
                const rsiPane = panes[indicators.get("rsi")?.pane];
                if (rsiPane) {
                    rsiPane.detachPrimitive(rsiPanePrimitiveRef.current);
                }
            }

            if (indicators.get("macd")?.enabled && indicators.get("macd")?.pane !== null && macdPanePrimitiveRef.current) {
                const macdPane = panes[indicators.get("macd")?.pane];
                if (macdPane) {
                    macdPane.detachPrimitive(macdPanePrimitiveRef.current);
                }
            }
        };
    }, [indicators, priceData, activeTab, chartType, technicalIndicators.rsi, itemIcon, itemName, currencyIcon]);

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex space-x-1">
                    {["Day", "Week", "Month", "3M", "6M", "1Y", "All"].map((tab) => (
                        <button
                            key={tab}
                            className={`px-4 py-1 text-sm rounded ${activeTab === tab ? "bg-blue-600" : "bg-[#2c2f33] hover:bg-[#3a3f45]"}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onClick={refreshData}
                        className={`p-1.5 flex items-center justify-center bg-[#2c2f33] hover:bg-[#3a3f45] rounded ${
                            isRefreshing ? "animate-spin" : ""
                        }`}
                        disabled={isRefreshing}
                        title="Refresh Data"
                    >
                        <RefreshCw size={16} />
                    </button>

                    <div className="flex bg-[#2c2f33] rounded overflow-hidden">
                        <button
                            className={cn("p-1.5 flex items-center justify-center", chartType === "line" ? "bg-blue-600" : "hover:bg-[#3a3f45]")}
                            onClick={() => setChartType("line")}
                            title="Line Chart"
                        >
                            <LineChart size={16} />
                        </button>
                        <button
                            className={cn("p-1.5 flex items-center justify-center", chartType === "candle" ? "bg-blue-600" : "hover:bg-[#3a3f45]")}
                            onClick={() => setChartType("candle")}
                            title="Candlestick Chart"
                        >
                            <BarChart3 size={16} />
                        </button>
                    </div>

                    <TradingTools
                        indicators={indicators}
                        onToggleIndicator={toggleIndicator}
                        handleColorChange={handleColorChange}
                        handleOptionChange={handleOptionChange}
                    />
                </div>
            </div>

            <div className="w-full h-[750px] relative bg-[#1e2124] rounded-lg pr-2">
                <div ref={chartContainerRef} className="w-full h-full" />
            </div>

            <style jsx>{`
                .pane-tooltip {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue",
                        sans-serif;
                }
            `}</style>
        </div>
    );
}
